import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { IconButton, Stack, Typography } from '@mui/material';
import { MIcon } from '../@material-extend';
import CloseButton from '../features/clockings/ClockingButtons/ClockingButton/CloseButton';

/**
 * WeekPicker Component
 *
 * A component that allows the user to navigate through weeks using left and right arrow buttons.
 * It validates the date range against optional minimum and maximum constraints.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.dates - The current start and end dates of the selected week.
 * @param {string} [props.label=''] - Label text displayed above the week picker.
 * @param {string} [props.minMonth=null] - The earliest month (formatted as 'YYYYMM') allowed for selection.
 * @param {string} [props.maxMonth=null] - The latest month (formatted as 'YYYYMM') allowed for selection.
 * @param {string} [props.minDate=null] - The earliest date (formatted as 'YYYY-MM-DD') allowed for selection.
 * @param {string} [props.maxDate=null] - The latest date (formatted as 'YYYY-MM-DD') allowed for selection.
 * @param {Object} [props.sx={}] - Additional styling for the container.
 * @param {Function} props.onChange - Callback function triggered when the week changes.
 * @param {Object} ref - Ref forwarded to the container element.
 *
 * @returns {JSX.Element} A week picker component.
 */
const WeekPicker = forwardRef((props, ref) => {
  const {
    dates = { start: null, end: null },
    label = '',
    minMonth = null,
    minDate = null,
    maxDate = null,
    maxMonth = null,
    sx,
    onChange,
    ...other
  } = props;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  /**
   * Navigate to the next week, respecting the maxDate and maxMonth constraints.
   */
  const nextWeek = () => {
    const msgDate = maxDate || maxMonth;
    const msgDateSourceFormat = maxMonth ? 'YYYYMM' : 'YYYY-MM-DD';
    const msgDateTargetFormat = maxMonth ? 'MMMM' : 'DD/MM/YYYY';
    const monthOrDate = moment(msgDate, msgDateSourceFormat).format(msgDateTargetFormat);
    const msg = `Vous ne pouvez pas aller au-delà ${maxMonth ? 'du mois de' : 'du'} ${monthOrDate}.`;

    let dateStart = moment(dates.start).add(1, 'W');
    const startMonth = dateStart.format('YYYYMM');
    if (maxMonth && startMonth > maxMonth) {
      dateStart = moment(maxMonth, 'YYYYMM').endOf('month');
    }

    const startOfWeek = moment(dateStart).startOf('week');
    const startOfMaxWeek = moment(maxDate, 'YYYY-MM-DD').startOf('week');
    if (maxDate && startOfWeek > startOfMaxWeek) {
      return enqueueSnackbar(msg, {
        variant: 'warning',
        action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
      });
    }

    onChange(dateStart);
  };

  /**
   * Navigate to the previous week, respecting the minDate and minMonth constraints.
   */
  const previousWeek = () => {
    const msgDate = minMonth || minDate;
    const msgDateSourceFormat = minMonth ? 'YYYYMM' : 'YYYY-MM-DD';
    const msgDateTargetFormat = minMonth ? 'MMMM' : 'DD/MM/YYYY';
    const monthOrDate = moment(msgDate, msgDateSourceFormat).format(msgDateTargetFormat);
    const msg = `Vous ne pouvez pas aller en deçà ${minMonth ? 'du mois de' : 'du'} ${monthOrDate}.`;

    let dateStart = moment(dates.start).add(-1, 'W');
    const startMonth = dateStart.format('YYYYMM');
    if (minMonth && startMonth < minMonth) {
      dateStart = moment(minMonth, 'YYYYMM').startOf('month');
    }

    if (minDate && moment(dateStart) < moment(minDate, 'YYYY-MM-DD').startOf('week')) {
      return enqueueSnackbar(msg, {
        variant: 'warning',
        action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
      });
    }

    onChange(dateStart);
  };

  return (
    <>
      {label.length > 0 && (
        <Typography align="center" variant="caption" sx={{ fontWeight: '600', ml: 1, mr: 1 }}>
          {label}
        </Typography>
      )}

      <Stack ref={ref} direction="row" alignItems="center" gap={1} sx={styleContainer(sx)}>
        <IconButton id="previous-week" color="primary" aria-label="previous-week" onClick={previousWeek} {...other}>
          <MIcon icon="chevron_left" fontSize={20} />
        </IconButton>

        <Typography align="center" fontSize={14} sx={styleText}>
          {moment(dates.start).format('DD/MM/YY')} - {moment(dates.end).format('DD/MM/YY')}
        </Typography>

        <IconButton id="next-week" color="primary" aria-label="next-week" onClick={nextWeek} {...other}>
          <MIcon icon="chevron_right" fontSize={20} />
        </IconButton>
      </Stack>
    </>
  );
});

const styleContainer = (sx) => ({
  border: 1,
  borderColor: (theme) => theme.palette.primary.main,
  borderRadius: 1,
  px: 0.25,
  ...sx
});

const styleText = {
  flexGrow: 1,
  fontWeight: 600,
  whiteSpace: 'nowrap'
};

WeekPicker.propTypes = {
  dates: PropTypes.object.isRequired,
  label: PropTypes.string,
  minMonth: PropTypes.number,
  maxMonth: PropTypes.number,
  minDate: PropTypes.PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.instanceOf(Date), PropTypes.string]),
  maxDate: PropTypes.PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.instanceOf(Date), PropTypes.string]),
  sx: PropTypes.object,
  onChange: PropTypes.func.isRequired
};

export default WeekPicker;
